import {
  AllowanceTypesToHandle,
  BookingState,
  convertToDecimals,
  distanceUnit,
  IAdminRequestedDetailsData,
  ICalendarEventType,
  IPharmacistDetails,
  IWorkshift,
  PharmacyLegendVariant,
  TypeOfSummary,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import useAllowanceWorkshiftDropdowns from '../workshift/useAllowanceWorkshiftDropdowns';
import { getTimeWithDiff } from '../../components/Modals/WorkshiftDetails/utils';
import classes from '../../components/SelfService/PharmacistSummary/styles';
import { getDayMonthDateYear } from '../../components/Reports/helpers';
import { repeatEvery } from '../../helpers/Constants';
import { useAppSelector } from '../useAppSelector';
import {
  displayIcon,
  handleValues,
} from '../../components/SelfService/PharmacistSummary/helpers';
import strings from '../../localization';
import useSelfService from '../useSelfService';

interface IUsePharmacistSummary {
  type: ICalendarEventType;
  summaryType?: TypeOfSummary;
  event: Partial<IWorkshift> &
  Partial<IPharmacistDetails> &
  Partial<IAdminRequestedDetailsData>;
  DateTime?: React.ReactNode | null;
  isAutoSyncRecord?:boolean;
}
const { pharmacist, workshiftDetails } = TypeOfSummary;
const { booked } = PharmacyLegendVariant;
const { UpdatedTiming } = BookingState;

const { paidAirTicket, paidValuePerNight } = AllowanceTypesToHandle;

const { recurrencePadding, workshiftContainer } = classes;

const usePharmacistSummary = ({
  type,
  summaryType,
  event,
  DateTime,
  isAutoSyncRecord,
}: IUsePharmacistSummary) => {
  const {
    bookingState,
    counterEndDate = '',
    counterStartDate = '',
    startDate = '',
    endDate = '',
    recurrence,
    emergencyFees,
    accommodationAllowanceType,
    travelAllowanceTypes,
    accommodationAllowance,
    mealAllowance,
    paidHourlyRate = 0,
    travelAllowanceRatePerKm,
    travelAllowanceAmount,
    travelAllowance,
    counterHourlyRate = 0,
    otherAllowance,
  } = event ?? {};

  const { isSelfService } = useSelfService();

  const isBooked = type === booked;
  const isPharmacistSummary = summaryType === pharmacist;
  const isWorkshiftDetails = summaryType === workshiftDetails;
  const isUpdatedTiming = bookingState === UpdatedTiming && isBooked;
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const isTravelGiven = !!travelAllowanceTypes?.filter((item) =>
    item !== '')
    ?.length;
  const isAccommodationGiven = !!accommodationAllowanceType;
  const isMealGiven = !!mealAllowance;

  const { travelDropdowns, accommodationDropdowns } = useAllowanceWorkshiftDropdowns({});

  const paidAirTicketGiven = travelAllowanceTypes?.includes(paidAirTicket)
    && travelDropdowns?.find((item) =>
      item.key === paidAirTicket)?.value;
  const accommodationLabels = accommodationAllowanceType !== paidValuePerNight
    && accommodationDropdowns?.find(
      (item) =>
        item.key === accommodationAllowanceType,
    )?.value;

  const updatedTimings = isUpdatedTiming
    ? {
      startDate: counterStartDate,
      endDate: counterEndDate,
    }
    : {
      startDate,
      endDate,
    };

  const { startDate: updatedStartDate, endDate: updatedEndDate } = updatedTimings ?? {};

  const hourlyRate = {
    paidHourlyRate,
    counterHourlyRate,
  };

  const recurrenceRepeat = repeatEvery()[
    (recurrence?.recurrenceType as keyof typeof repeatEvery) ?? 0
  ];

  const hours = getTimeWithDiff(updatedStartDate, updatedEndDate, timeFormat);
  const counterHours = getTimeWithDiff(
    counterStartDate,
    counterEndDate,
    timeFormat,
  );
  const dateLabel = `${getDayMonthDateYear(startDate)} | ${hours}`;
  const counterLabel = isUpdatedTiming ? hours : '';
  const counterDateLabel = `${getDayMonthDateYear(
    counterStartDate,
  )} | ${counterHours}`;

  const recurrenceStyle = DateTime ? recurrencePadding : workshiftContainer;
  const travelPerKm = !!travelAllowanceRatePerKm
    && `$${travelAllowanceRatePerKm}/${distanceUnit} `;
  const accommodationPerNight = !!accommodationAllowance && `$${accommodationAllowance} /${strings.night}`;

  let travelDisplay = displayIcon(false, '');
  let accommodationDisplay = displayIcon(false, '');
  let mealDisplay = displayIcon(false, '');
  if (isSelfService && !isAutoSyncRecord) {
    travelDisplay = displayIcon(
      isTravelGiven,
      handleValues([travelAllowanceAmount, travelPerKm, paidAirTicketGiven]),
    );

    accommodationDisplay = displayIcon(
      isAccommodationGiven,
      handleValues([accommodationPerNight, accommodationLabels]),
    );

    mealDisplay = displayIcon(isMealGiven, handleValues([mealAllowance]));
  } else {
    travelDisplay = displayIcon(!!travelAllowance, handleValues([travelAllowance]));
    accommodationDisplay = displayIcon(!!accommodationAllowance, handleValues([accommodationAllowance]));
    mealDisplay = displayIcon(isMealGiven, handleValues([mealAllowance]));
  }

  const { data: travelData, icon: travelIcon } = travelDisplay;
  const { data: accommodationData, icon: accommodationIcon } = accommodationDisplay;
  const { data: mealData, icon: mealIcon } = mealDisplay;

  const travelLabel = `${strings.travel}: ${travelData}`;

  const emergencyFeesLabel = `${strings.emergencyServiceFees
  }: $${convertToDecimals(emergencyFees)}`;

  const otherAllowanceLabel = `${strings.other}: $${convertToDecimals(
    otherAllowance,
  )}`;

  const accomodationLabel = `${strings.accomodation}: ${accommodationData}`;

  const mealLabel = `${strings.meal}: ${mealData}`;

  return {
    emergencyFeesLabel,
    otherAllowanceLabel,
    accomodationLabel,
    mealLabel,
    travelLabel,
    travelIcon,
    accommodationIcon,
    mealIcon,
    recurrenceStyle,
    dateLabel,
    counterDateLabel,
    hourlyRate,
    recurrenceRepeat,
    counterLabel,
    isPharmacistSummary,
    isWorkshiftDetails,
  };
};

export default usePharmacistSummary;
